import type {StateCreator} from 'zustand'
import type {IssueComment} from '../../page-data/payloads/issue-comments'

export type IssueCommentSlice = {
  issueComments: IssueComment[]
  issueCommentsLoaded: boolean

  commentsPanelActions: {
    setIssueComments: (comments: IssueComment[]) => void
    addIssueComment: (comment: IssueComment) => void
    addIssueComments: (comments: IssueComment[]) => void
    upsertIssueComment: (comment: IssueComment) => void
    updateIssueComment: (args: {
      match: (comment: IssueComment) => boolean
      transform: (comment: IssueComment | undefined) => IssueComment | undefined
    }) => void
  }
}

export const createIssueCommentSlice =
  (): StateCreator<IssueCommentSlice, [['zustand/devtools', never]], [], IssueCommentSlice> => (set, _get) => ({
    issueComments: [],
    issueCommentsLoaded: false,

    commentsPanelActions: {
      setIssueComments: comments => {
        set({issueComments: comments, issueCommentsLoaded: true}, undefined, 'setIssueComments')
      },
      addIssueComments: comments => {
        set(
          state => {
            return {
              issueComments: [...state.issueComments, ...comments],
              issueCommentsLoaded: true,
            }
          },
          undefined,
          'addIssueComments',
        )
      },
      addIssueComment: comment => {
        set(
          state => {
            return {
              issueComments: [...state.issueComments, comment],
            }
          },
          undefined,
          'addIssueComment',
        )
      },
      upsertIssueComment: comment => {
        set(
          state => {
            const existingIndex = state.issueComments.findIndex(existingComment => existingComment.id === comment.id)
            if (existingIndex === -1) {
              return {issueComments: [...state.issueComments, comment]}
            }

            const issueComments = [...state.issueComments]
            issueComments[existingIndex] = comment
            return {issueComments}
          },
          undefined,
          'upsertIssueComment',
        )
      },

      updateIssueComment: ({match, transform}) => {
        set(
          state => ({
            issueComments: state.issueComments.map(comment => {
              if (match(comment)) {
                const updated = transform(comment)
                return updated ?? comment
              }
              return comment
            }),
          }),
          undefined,
          'updateIssueComment',
        )
      },
    },
  })
